import type { TenantChrome } from '@blooq/flow'
import { IonSkeletonText } from '@ionic/react'
import type { ReactNode } from 'react'

import { monogram } from '../lib/theme'

import type { Turn } from './Conversation'
import { DotSphere, STILL } from './DotSphere'

/*
 * The conversation above the dock.
 *
 * Read-only, always. Every control is in the dock, and the reason is that a
 * transcript with live buttons scattered through it is a screen where the
 * customer has to look for what to do next — on the third step, after the
 * second scroll, on a phone in one hand.
 */

/**
 * The business, as the face beside its messages.
 *
 * Its logo where it has one, its monogram where it does not — the same rule
 * the header and the app icon follow, so the three agree. Consecutive bot
 * messages show it once; the stylesheet hides the repeats.
 */
export function Avatar({ tenant }: { tenant?: TenantChrome }) {
  if (tenant?.logoUrl) return <img className="blooq-msg__avatar" src={tenant.logoUrl} alt="" />
  return (
    <span className="blooq-msg__avatar" aria-hidden="true">
      {monogram(tenant?.businessName ?? '')}
    </span>
  )
}

export function Bubble({
  kind,
  lead,
  card,
  avatar,
  children,
}: {
  kind: 'bot' | 'user'
  /** The step being answered now, rather than one already behind us. */
  lead?: boolean
  /** A message with rows of actions attached under its text. */
  card?: boolean
  /** Shown beside a bot message. Ignored for the customer's own. */
  avatar?: ReactNode
  children: ReactNode
}) {
  return (
    <div className={`blooq-msg blooq-msg--${kind}`}>
      {kind === 'bot' ? avatar : null}
      <div
        className={`blooq-bubble blooq-bubble--${kind}${lead ? ' blooq-bubble--lead' : ''}${card ? ' blooq-bubble--card' : ''}`}
        /*
         * The live one is announced; the log is not.
         *
         * A screen reader that re-reads the whole transcript on every step
         * turns a four-step booking into a recital. `aria-live` on the current
         * prompt says the one thing that changed.
         */
        aria-live={lead ? 'polite' : undefined}
      >
        {children}
      </div>
    </div>
  )
}

/** One completed exchange: what was asked, and what was answered. */
export function PastTurn({ turn, avatar }: { turn: Turn; avatar?: ReactNode }) {
  return (
    <>
      {turn.prompt ? (
        <Bubble kind="bot" avatar={avatar}>
          {turn.prompt}
        </Bubble>
      ) : null}
      {turn.answer ? (
        <Bubble kind="user">{turn.answer}</Bubble>
      ) : turn.skipped ? (
        <div className="blooq-msg blooq-msg--user">
          <span className="blooq-skipped">Skipped</span>
        </div>
      ) : null}
    </>
  )
}

/**
 * The business is thinking.
 *
 * The front door's sphere, small and turning fast, in the place the next
 * bubble will land — so the thing that answers is the thing the customer met
 * on the first screen, and nothing jumps when the answer arrives. Not a
 * spinner: a spinner in a chat reads as an error about to happen.
 */
export function Typing({ avatar, accent }: { avatar?: ReactNode; accent?: string }) {
  return (
    <div className="blooq-msg blooq-msg--bot">
      {avatar}
      <div className="blooq-typing" role="status" aria-label="Working">
        <DotSphere
          accent={accent ?? '#0369a1'}
          mode="thinking"
          level={STILL}
          points={110}
          interactive={false}
        />
      </div>
    </div>
  )
}

/**
 * The first paint, before the business is known.
 *
 * Shaped like the front door it is standing in for — a name in the middle of
 * the screen. The web app renders its first step on the server and never
 * shows this; a static bundle has one round trip to cover, and covering it
 * with the right silhouette is what keeps that trip from reading as a blank
 * screen.
 */
export function Skeleton() {
  return (
    <div className="blooq-skeleton" aria-hidden="true">
      <div className="blooq-skeleton__stage">
        <IonSkeletonText animated style={{ width: '64%', height: 8, borderRadius: 4 }} />
        <IonSkeletonText animated style={{ width: '62%', height: 26, borderRadius: 8 }} />
      </div>
    </div>
  )
}
